import apiClient from "./client";
import type {
  Answer,
  Paginated,
  Session,
  SessionScore,
  Skill,
  StartSessionPayload,
  SubmitSessionPayload,
} from "@/types";

/**
 * Exam sessions API (shared by listening, reading and writing).
 * Backend endpoints:
 *   POST   /sessions                  -> start a session
 *   GET    /sessions                  -> list current user's sessions
 *   GET    /sessions/{id}             -> session detail (answers, submissions, score)
 *   PUT    /sessions/{id}/autosave    -> upsert answers
 *   POST   /sessions/{id}/submit      -> final submit + grading
 *   GET    /scores/sessions/{id}      -> score for a session
 */

export async function startSession(
  payload: StartSessionPayload
): Promise<Session> {
  const { data } = await apiClient.post<Session>("/sessions", payload);
  return data;
}

export async function getSession(sessionId: number): Promise<Session> {
  const { data } = await apiClient.get<Session>(`/sessions/${sessionId}`);
  return data;
}

export async function listSessions({
  page = 1,
  size = 50,
}: { page?: number; size?: number } = {}): Promise<Paginated<Session>> {
  const { data } = await apiClient.get<Paginated<Session>>("/sessions", {
    params: { page, size },
  });
  return data;
}

export async function autosave(
  sessionId: number,
  answers: Answer[]
): Promise<Session> {
  const { data } = await apiClient.put<Session>(
    `/sessions/${sessionId}/autosave`,
    { answers }
  );
  return data;
}

export async function submitSession(
  sessionId: number,
  { answers = [], writing = [] }: SubmitSessionPayload = {}
): Promise<Session> {
  const { data } = await apiClient.post<Session>(
    `/sessions/${sessionId}/submit`,
    { answers, writing }
  );
  return data;
}

export async function getSessionScore(
  sessionId: number
): Promise<SessionScore> {
  const { data } = await apiClient.get<SessionScore>(
    `/scores/sessions/${sessionId}`
  );
  return data;
}

// Find an in-progress session for a given test + skill, to support resume.
export async function findInProgressSession(
  test_id: number,
  skill: Skill
): Promise<Session | null> {
  const { items } = await listSessions({ page: 1, size: 100 });
  return (
    items.find(
      (s) =>
        Number(s.test_id) === Number(test_id) &&
        s.skill === skill &&
        s.status === "in_progress"
    ) || null
  );
}
